import { useCallback, useMemo, useRef, useState } from "react";
import { HeaderProps } from "../types/header-props";
import { SortDirection } from "../types/sort-direction";
import { TableBody } from "./table-body";
import { TableHead } from "./table-head";
import { TableFilter, TableFilterProps } from "./table-filter";
import { useFilterableData } from "../hooks/use-filterable-data";
import { Sort } from "../types/sort";
import { CompareFunction } from "../types/compare-function";
import { TableRowProps } from "../types/table-row-props";
import { defaultSort } from "../services/default-sort";
import { EmptyTableRow } from "./empty-table-row";
import { useMobileLayout } from "../hooks/use-mobile-layout";

export interface TableProps<TData, TSort> {
    data: TData[] | undefined | null;
    columns?: HeaderProps<TSort>[];
    sortFunction?: (sort: Sort<TSort>) => CompareFunction<TData>;
    render: React.FunctionComponent<TableRowProps<TData>>
    striped?: boolean;
    hover?: boolean;
    bordered?: boolean;
    borderless?: boolean;
    small?: boolean;
    className?: string;
    defaultSort?: TSort;
    defaultDirection?: SortDirection;
    EmptyTableRow?: React.ReactNode;
    EmptyTable?: React.ReactNode;
    filter?: string;
    filterFunction?: (term: string) => (data: TData) => boolean;
    lazy?: { initialItems: number, itemsPerPage: number };
    SortIcon?: React.FunctionComponent<{ isActive: boolean, direction: SortDirection }>;
    Filter?: React.FunctionComponent<TableFilterProps>
    shiftToMobile?: ((width: number) => boolean) | number;
    idProp?: keyof TData;
    onAdvance?: {
        next?: () => void;
    };
}

export function Table<TData, TSort = string>({ shiftToMobile, idProp, ...props }: TableProps<TData, TSort>) {

    const shiftToMobileCheck = useMemo(() => typeof shiftToMobile === 'number' ? ((width: number) => width < shiftToMobile) : shiftToMobile, [shiftToMobile])

    const containerRef = useRef<HTMLDivElement>(null);
    const { isMobileLayout } = useMobileLayout({
        shiftToMobile: shiftToMobileCheck,
        containerRef
    });

    const [filterTerm, setFilterTerm] = useState('');
    const [sort, setSort] = useState<Sort<TSort> | undefined>(defaultSort(props));
    const filteredData = useFilterableData(props.data, props.filter || filterTerm, props.filterFunction);

    const classes = [
        'table',
        props.striped && 'table-striped',
        props.hover && 'table-hover',
        props.bordered && 'table-bordered',
        props.borderless && 'table-borderless',
        props.small && 'table-sm',
        props.className
    ].filter(x => x).join(' ');

    const isEmpty = props.data?.length === 0;
    const Filter = props.Filter || TableFilter;

    return (
        <div ref={containerRef} className="table-container w-100">
            <Filter visible={!!props.filterFunction} filterTerm={filterTerm} setFilterTerm={setFilterTerm} />
            {isEmpty && props.EmptyTable ? props.EmptyTable :
                <table className={classes}>
                    {isMobileLayout ? null : <TableHead sort={sort} setSort={setSort} columns={props.columns} SortIcon={props.SortIcon} />}
                    <TableBody
                        onAdvance={props.onAdvance}
                        idProp={idProp}
                        useMobileLayout={isMobileLayout}
                        data={filteredData}
                        render={props.render}
                        lazy={props.lazy}
                        sort={sort} sortFunction={props.sortFunction}
                        EmptyTable={props.EmptyTableRow || <EmptyTableRow columns={props.columns} />
                        } />
                </table>}
        </div>
    );
}
